// src/tenantAdmin/pages/TenantEditUserModal.jsx
// Tenant-admin "Modifica utente" modal (Brian UI). Self-contained overlay/card
// with Escape-to-close (only while it is the top-most modal — components/ui/
// modalStack.js), seeded from the list row (no detail fetch).
//
// What tenant-admins can actually change (BE): NOME (first_name, via
// PATCH /tenant-admin/users/{id}/profile), RUOLO (via .../role) and the
// CREDENTIAL (via POST .../password — the route is live; the note that once
// claimed otherwise was stale). EMAIL is superadmin-only, so it's shown read-only.
//
// ⚠️ The profile PATCH and the credential POST can both answer 409 "this person
// belongs elsewhere" (other companies / other workspaces). The step that refused
// decides the wording, so handleSubmit tracks it — see membershipRefusal.js.
// Everything else is worded by userActionError.js; ⛔ no surface here ever renders
// the server's own sentence.
//
// ⛔ The OWN row cannot reset its own credential from here (the backend answers
// 409 CANNOT_RESET_OWN_PASSWORD): the password section is disabled on it, exactly
// as the role select already is, and points at Profilo → Cambia password. The 409
// copy stays mapped as a backstop.
//
// PATCH semantics for the name: send it only when it actually changed; a field
// emptied by the user is sent as explicit null (clear). last_name/locale are left
// untouched (this modal doesn't edit them), so they are never sent.
//
// ⛔ `tenantId` is REQUIRED: every tenantAdminApi wrapper names its workspace in
// the query and throws a TypeError without one. The dialog never falls back to the
// active workspace — editing the right person in the wrong workspace is the bug
// the argument exists to prevent.
import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { User } from 'lucide-react';
import { updateTenantUserProfile, changeTenantUserRole, resetTenantUserPassword } from '../tenantAdminApi.js';
import { membershipRefusalCopy } from '../membershipRefusal.js';
import { userActionErrorCopy } from '../userActionError.js';
import { generatePassword } from '../../utils/generatePassword.js';
import { COLORS, SEMANTIC, RADII, FONT_SANS, SHADOW } from '../../constants/theme.js';
import { useContentInset } from '../../components/ui/modalArea.js';
import { useModalLayer } from '../../components/ui/modalStack.js';

const fieldLabel = {
  display: 'block', fontSize: '11px', fontWeight: 600, letterSpacing: '0.6px',
  textTransform: 'uppercase', color: COLORS.textSecondary, fontFamily: FONT_SANS,
  marginBottom: '8px',
};
const field = {
  width: '100%', height: '46px', boxSizing: 'border-box',
  background: COLORS.surface, border: `1px solid ${COLORS.border}`,
  borderRadius: RADII.control, color: COLORS.ink, fontSize: '14px',
  fontFamily: FONT_SANS, outline: 'none', padding: '0 14px',
};

// Blank/whitespace becomes null (clear); otherwise the trimmed string.
function normalizeName(s) {
  const trimmed = (s || '').trim();
  return trimmed === '' ? null : trimmed;
}

export default function TenantEditUserModal({ open, tenantId, user, currentUserId, onClose, onSaved }) {
  // Centred in the page's content area (beside the sidebar), like every app modal — components/ui/modalArea.js.
  const insetLeft = useContentInset(open);
  const isTop = useModalLayer(open);
  const { t } = useTranslation('admin');

  const [name, setName] = useState('');
  const [role, setRole] = useState('editor');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // Seed the form from the list row each time the modal opens on a user. Password
  // starts empty (you can't read a stored password) — set it only to reset it.
  useEffect(() => {
    if (!open || !user) return;
    setName(user.first_name || '');
    setRole(user.role || 'editor');
    setPassword('');
    setError('');
  }, [open, user]);

  useEffect(() => {
    if (!open) return;
    const handler = (e) => { if (e.key === 'Escape' && isTop()) handleClose(); };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const isSelf = !!user && user.user_id === currentUserId;
  const nextName = normalizeName(name);
  const nameChanged = nextName !== (user?.first_name ?? null);
  const roleChanged = !isSelf && role !== (user?.role ?? 'editor');
  const newPassword = password.trim();
  // The own credential is never reset from here — see the header note.
  const passwordChanged = !isSelf && newPassword.length > 0;
  const dirty = nameChanged || roleChanged || passwordChanged;

  async function handleSubmit(e) {
    e.preventDefault();
    if (!dirty || !user) return;
    setError('');
    setLoading(true);
    // Which write is in flight. The two belongs-elsewhere 409s carry no hint of
    // the action, and the remedy differs per action, so the step is what names it.
    let step = null;
    try {
      // Name first (its 404/403 codes are the informative ones), then role, then
      // password last (so a failing reset doesn't block the name/role edits).
      // A rejection stops here: no later step is attempted.
      if (nameChanged) { step = 'editProfile'; await updateTenantUserProfile(tenantId, user.user_id, { first_name: nextName }); }
      if (roleChanged) { step = 'changeRole'; await changeTenantUserRole(tenantId, user.user_id, role); }
      if (passwordChanged) { step = 'resetPassword'; await resetTenantUserPassword(tenantId, user.user_id, newPassword); }
      onSaved();
      onClose();
    } catch (err) {
      // Map err.code (never the message text) to UX.
      const membershipRefusal = membershipRefusalCopy(t, err, step);
      if (membershipRefusal) {
        // Nothing this dialog can do about it, and an earlier step may already
        // have landed — keep the modal open on our copy and refresh the list.
        setError(membershipRefusal);
        onSaved();
      } else if (err.code === 'CANNOT_EDIT_USER') {
        // BE message is deliberately generic — tenant-admin views never expose
        // is_superadmin, so we can't (and shouldn't) explain why.
        setError(t('tenantUsers.cannotEditUser'));
      } else if (err.code === 'USER_TENANT_ASSIGNMENT_NOT_FOUND' || err.code === 'USER_NOT_FOUND') {
        // The row is stale — surface it and refresh the underlying list.
        setError(t('tenantUsers.userGone'));
        onSaved();
      } else {
        // Partial success is possible (name saved, role failed) — refresh so the
        // list reflects whatever landed, and keep the modal open on OUR sentence.
        // CANNOT_RESET_OWN_PASSWORD is a backstop here: the section is disabled
        // on the own row.
        setError(userActionErrorCopy(t, err));
        onSaved();
      }
    } finally {
      setLoading(false);
    }
  }

  function handleClose() {
    setError('');
    onClose();
  }

  if (!open || !user) return null;

  const created = user.created_at ? new Date(user.created_at).toLocaleDateString('it-IT') : '—';

  const outlineBtn = {
    height: '46px', padding: '0 22px', borderRadius: RADII.control,
    border: `1px solid ${COLORS.borderStrong}`, background: COLORS.surface,
    color: COLORS.ink, fontSize: '14px', fontWeight: 600, cursor: 'pointer',
    fontFamily: FONT_SANS,
  };

  return (
    <div
      onClick={handleClose}
      style={{
        position: 'fixed', inset: 0, zIndex: 1000, background: 'rgba(35,0,62,0.35)',
        display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '24px',
        fontFamily: FONT_SANS,
        ...(insetLeft ? { paddingLeft: `${insetLeft + 24}px` } : null),
      }}
    >
      <div
        onClick={e => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-label={t('tenantUsers.editUser')}
        style={{
          background: COLORS.surface, borderRadius: RADII.section, boxShadow: SHADOW.card,
          border: `1px solid ${COLORS.border}`, width: '100%', maxWidth: '560px',
          maxHeight: '90vh', overflow: 'auto',
        }}
      >
        {/* Header — icon + email + created date */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', padding: '24px 24px 0' }}>
          <div style={{
            width: '40px', height: '40px', borderRadius: RADII.pill, flexShrink: 0,
            border: `1px solid ${COLORS.border}`,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}>
            <User size={20} color={COLORS.ink} strokeWidth={1.8} />
          </div>
          <div style={{ minWidth: 0 }}>
            <div style={{ fontSize: '16px', fontWeight: 700, lineHeight: '24px', color: COLORS.ink, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {user.email}
            </div>
            <div style={{ fontSize: '12px', color: COLORS.textTertiary }}>
              {t('tenantUsers.createdOn', { date: created })}
            </div>
          </div>
        </div>

        <form onSubmit={handleSubmit}>
          <div style={{ padding: '24px' }}>
            <div style={{ marginBottom: '20px' }}>
              <label htmlFor="teum-name" style={fieldLabel}>{t('profile.firstName')}</label>
              <input id="teum-name" type="text" value={name} maxLength={100} onChange={e => setName(e.target.value)} style={field} />
            </div>

            <div style={{ marginBottom: '20px' }}>
              <label htmlFor="teum-email" style={fieldLabel}>{t('shared.email')}</label>
              {/* Email is superadmin-only — read-only here (muted to signal that). */}
              <input
                id="teum-email"
                type="email"
                value={user.email}
                readOnly
                style={{ ...field, background: COLORS.field, color: COLORS.textSecondary, cursor: 'default' }}
              />
            </div>

            <div style={{ marginBottom: '20px' }}>
              <label htmlFor="teum-password" style={fieldLabel}>{t('users.password')}</label>
              <div style={{ display: 'flex', gap: '12px' }}>
                {/* Disabled on the own row, like the role select below. */}
                <input
                  id="teum-password"
                  type="text"
                  value={password}
                  onChange={e => setPassword(e.target.value)}
                  minLength={8}
                  disabled={isSelf}
                  title={isSelf ? t('tenantUsers.cannotResetOwnPassword') : undefined}
                  placeholder={t('tenantUsers.resetPasswordPlaceholder')}
                  style={{ ...field, flex: 1, cursor: isSelf ? 'not-allowed' : 'text' }}
                />
                <button
                  type="button"
                  onClick={() => setPassword(generatePassword())}
                  disabled={isSelf}
                  title={isSelf ? t('tenantUsers.cannotResetOwnPassword') : undefined}
                  style={{ ...outlineBtn, flexShrink: 0, opacity: isSelf ? 0.5 : 1, cursor: isSelf ? 'not-allowed' : 'pointer' }}
                >
                  {t('tenantUsers.resetPassword')}
                </button>
              </div>
              {isSelf && (
                <span style={{ fontSize: '11px', color: COLORS.textTertiary, marginTop: '6px', display: 'block' }}>
                  {t('tenantUsers.cannotResetOwnPassword')}
                </span>
              )}
              {passwordChanged && (
                <span style={{ fontSize: '11px', color: COLORS.textTertiary, marginTop: '6px', display: 'block' }}>{t('shared.minChars')}</span>
              )}
            </div>

            <div>
              <label htmlFor="teum-role" style={fieldLabel}>{t('shared.role')}</label>
              <select
                id="teum-role"
                value={role}
                disabled={isSelf}
                title={isSelf ? t('tenantUsers.cannotChangeOwnRole') : undefined}
                onChange={e => setRole(e.target.value)}
                style={{ ...field, cursor: isSelf ? 'not-allowed' : 'pointer' }}
              >
                <option value="viewer">Viewer</option>
                <option value="editor">Editor</option>
                <option value="admin">Admin</option>
              </select>
            </div>

            {error && (
              <div style={{
                marginTop: '20px', padding: '10px 14px', borderRadius: RADII.control,
                background: SEMANTIC.errore.bg, border: `1px solid ${SEMANTIC.errore.fg}33`,
                color: SEMANTIC.errore.fg, fontSize: '13px',
              }}>
                {error}
              </div>
            )}
          </div>

          {/* Footer */}
          <div style={{
            display: 'flex', justifyContent: 'flex-end', gap: '12px',
            padding: '16px 24px', borderTop: `1px solid ${COLORS.border}`,
          }}>
            <button type="button" onClick={handleClose} style={outlineBtn}>
              {t('common:actions.cancel')}
            </button>
            <button
              type="submit"
              disabled={loading || !dirty}
              style={{
                height: '46px', padding: '0 22px', borderRadius: RADII.control, border: 'none',
                background: (loading || !dirty) ? COLORS.borderStrong : COLORS.accent,
                color: COLORS.surface, fontSize: '14px', fontWeight: 600, fontFamily: FONT_SANS,
                cursor: (loading || !dirty) ? 'not-allowed' : 'pointer',
              }}
            >
              {loading ? t('profile.saving') : t('tenantUsers.updateUser')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
